import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Building2, Phone, Mail, Eye, Wrench } from 'lucide-react';
import { api } from '../../services/api';
import { PageHeader, DataTable, Column, SortConfig, SearchInput } from '../../components/ui';
import { Badge, Card, buttonClass } from '../../design-system';
import { formatPhone, formatCpfCnpj, formatDate } from '../../utils/format';
import { PAGE_SIZE } from '../../constants';
import type { TipoServico } from '../../types';

interface FornecedorList {
  id: number;
  nome: string;
  tipoPessoa: string;
  cpfCnpj: string | null;
  email: string | null;
  telefone: string | null;
  celular: string | null;
  cidade: string | null;
  uf: string | null;
  dataCadastro: string;
  servicos: string[];
}

interface PagedResult {
  items: FornecedorList[];
  totalItems: number;
  page: number;
  pageSize: number;
}

export function FornecedoresList() {
  const navigate = useNavigate();
  const [data, setData] = useState<PagedResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [busca, setBusca] = useState('');
  const [tipoServico, setTipoServico] = useState('');
  const [tiposServico, setTiposServico] = useState<TipoServico[]>([]);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'nome', direction: 'asc' });

  // Carregar tipos de serviço
  useEffect(() => {
    const controller = new AbortController();
    api.get<TipoServico[]>('/fornecedores/tipos-servico', { signal: controller.signal })
      .then((response) => setTiposServico(response.data))
      .catch(() => { /* Ignore */ });
    return () => controller.abort();
  }, []);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: PAGE_SIZE.toString(),
      });
      if (busca) params.append('busca', busca);
      if (tipoServico) params.append('tipoServico', tipoServico);
      if (sortConfig.direction) {
        params.append('orderBy', sortConfig.key);
        params.append('orderDirection', sortConfig.direction);
      }

      const response = await api.get<PagedResult>(`/fornecedores?${params}`);
      setData(response.data);
    } catch (error) {
      console.error('Erro ao buscar fornecedores:', error);
    } finally {
      setIsLoading(false);
    }
  }, [page, busca, tipoServico, sortConfig]);

  const handleSort = (config: SortConfig) => {
    setSortConfig(config);
    setPage(1);
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchData();
    return () => controller.abort();
  }, [fetchData]);

  const columns: Column<FornecedorList>[] = [
    {
      key: 'nome',
      header: 'Fornecedor',
      sortable: true,
      render: (item) => (
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-50 ring-1 ring-primary-100">
            <Building2 className="h-5 w-5 text-primary-600" />
          </div>
          <div>
            <div className="font-medium text-slate-900">{item.nome}</div>
            {item.cpfCnpj && (
              <div className="text-sm text-slate-500">{formatCpfCnpj(item.cpfCnpj)}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'servicos',
      header: 'Serviços',
      render: (item) => (
        <div className="flex flex-wrap gap-1">
          {item.servicos.length > 0 ? (
            item.servicos.slice(0, 3).map((servico, idx) => (
              <Badge key={idx} tone="primary">
                {servico}
              </Badge>
            ))
          ) : (
            <span className="text-sm text-slate-400">Nenhum serviço</span>
          )}
          {item.servicos.length > 3 && (
            <Badge tone="neutral">+{item.servicos.length - 3}</Badge>
          )}
        </div>
      ),
    },
    {
      key: 'celular',
      header: 'Contato',
      sortable: true,
      render: (item) => (
        <div className="space-y-1">
          {(item.celular || item.telefone) && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Phone className="h-4 w-4 text-slate-400" />
              <span>{formatPhone(item.celular || item.telefone || '')}</span>
            </div>
          )}
          {item.email && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Mail className="h-4 w-4 text-slate-400" />
              <span className="max-w-[200px] truncate">{item.email}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'cidade',
      header: 'Cidade',
      sortable: true,
      render: (item) => (
        item.cidade ? `${item.cidade}${item.uf ? ` - ${item.uf}` : ''}` : '-'
      ),
    },
    {
      key: 'dataCadastro',
      header: 'Cadastro',
      sortable: true,
      render: (item) => formatDate(item.dataCadastro),
    },
    {
      key: 'actions',
      header: '',
      width: '50px',
      render: (item) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/fornecedores/${item.id}`);
          }}
          className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-primary-600"
          title="Ver detalhes"
        >
          <Eye className="h-4 w-4" />
        </button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="Fornecedores" subtitle="Gerencie seus fornecedores e prestadores de serviço">
        <button
          onClick={() => navigate('/fornecedores/novo')}
          className={buttonClass('primary')}
        >
          <Plus className="h-4 w-4" />
          Novo Fornecedor
        </button>
      </PageHeader>

      {/* Filters */}
      <Card className="mb-6 p-4">
        <div className="flex flex-wrap gap-4">
          <SearchInput
            value={busca}
            onChange={setBusca}
            placeholder="Buscar por nome, CNPJ/CPF, email ou telefone..."
          />
          <div className="relative min-w-[200px]">
            <Wrench className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <select
              value={tipoServico}
              onChange={(e) => {
                setTipoServico(e.target.value);
                setPage(1);
              }}
              className="h-10 w-full appearance-none rounded-xl border border-slate-200 bg-white pl-10 pr-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            >
              <option value="">Todos os serviços</option>
              {tiposServico.map((tipo) => (
                <option key={tipo.id} value={tipo.codigo}>
                  {tipo.nome}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Table */}
      <DataTable
        columns={columns}
        data={data?.items || []}
        keyField="id"
        isLoading={isLoading}
        emptyMessage="Nenhum fornecedor encontrado"
        page={page}
        pageSize={PAGE_SIZE}
        totalItems={data?.totalItems}
        onPageChange={setPage}
        sortConfig={sortConfig}
        onSort={handleSort}
        onRowClick={(item) => navigate(`/fornecedores/${item.id}`)}
      />
    </div>
  );
}
